import Vue from 'vue';
import UsedVehiclesAPI from '../../api/UsedVehiclesAPI';
import UsedVehicleFinance from '../../api/finance/UsedVehicleFinance';
import getOptimisedRouteFromRoute from '../../api/GetOptimisedRouteFromRoute';
import isLooselySameValue from '../../api/used-search-options/IsLooselySameValue';
import isSameArray from '../../api/used-search-options/IsSameArray';
import fillMissingKeysWithDefaultValues from '../../api/used-search-options/FillMissingKeysWithDefaultValues';
import getDefaultUserOptions from '../../api/used-search-options/DefaultValues';
import getAvailableUserOptionKeys from '../../api/used-search-options/AvailableKeys';
import router from './../../routes';
require('../../../modules/polyfills/object-assign');
const TWEEN = require('@tweenjs/tween.js');
import {saveToStorage} from './../../api/Storage';
import GetMetaDataFromRoute from '../../api/GetMetaDataFromRoute';
import debounce from 'lodash/debounce';
import { calculateDistance } from '../../api/LocationFunctions';
let loadingTimeout;
         
let animatedCountTween;
let animationFrame;
const animate = function(currentTime) {
    animationFrame = requestAnimationFrame(animate);
    TWEEN.update();
}
let animatedCountDestination;

const haveOptionsChanged = function(originalOptions, newOptions) {
    let somethingChanged = false;
    // Step through each of the user options that, if changed, mean the search results are invalid
    validUserOptions.forEach((key) => {
        if (somethingChanged == true) {
            return;
        }

        if (typeof originalOptions[key] !== 'undefined' && originalOptions[key].length == 0) {
            if (typeof newOptions[key] !== 'undefined' && newOptions[key].length > 0) {
                somethingChanged = true;
                return;
            }
            return;
        }

        if (typeof originalOptions[key] == 'object') {
            if (!isSameArray(originalOptions[key], newOptions[key])) {
                somethingChanged = true;
                return;
            }
            return;
        }

        if (!isLooselySameValue(originalOptions[key], newOptions[key])) {
            somethingChanged = true;
            return;
        }
    });
    return somethingChanged;
};

const validUserOptions = getAvailableUserOptionKeys();

const state = {
    lastResultsUrl: false,
    results: [],
    resultsCollected: false,
    haveResults: false,
    pages: [],
    defaultTerm: 48,
    defaultDeposit: 2315,
    defaultAnnualMileage: 10000,
    options: {
        model: [],
        trim: [],
        manufacturer: [],
        minPrice: null,
        maxPrice: null,
        monthlyPayments: {
            min: 0,
            max: 2000,
        },
        age: null,
        fuelType: null,
        numDoors: null,
        body: null,
        mileage: null,
        transmission: null,
        colour: null,
        engineSize: null,
        count: 0,
        emissions: []
    },
    animatedCount: 0,
    distances: [
        -1,
        5,
        10,
        20,
        30,
        40,
        50,
    ],
    userOptions: getDefaultUserOptions(),
    loading: true,
    resultsLoading: true,
    loadingTimeout: false,
    refineSearchVisible: false,
    refineSortVisible: false,
    firstLoad: true,
    sortByModified: false,

    // Holds all of the manufacturer + model combinations for available landing pages
    // This gets filled the first time a landing page is called
    usedLandingPages: {},
    usedLandingPagesCollected: false,
    usedVehicleModelLandingPageData: null,
    seoTitle: null,
    seoDescription: null,

    // Holds all of the dealer slugs in a hash lookup table
    // This gets filled the first time a dealer-landing-page route is viewed
    dealerLandingPage: null,

    // Array version of used dealers used for location sorting. Putting it here instead of the dealers vuex store because the dealers store is used on the OSB and test drive widget.
    usedDealers: [],

    notifications: [],
};


// getters
const getters = {
    count(state) {
        if (state.options.count) {
            return state.options.count;
        }
        return 0;
    },
    distances(state) {
        return state.distances;
    },
    nearlyNew(state) {
        return state.userOptions.nearlyNew;
    },
    preReg(state) {
        return state.userOptions.preReg;
    },
    preRegId(state) {
        return state.userOptions.preRegId;
    },
    models(state) {
        if (state.options.model) {
            return state.options.model;
        }
        return [];
    },
    trims(state) {
        if (state.options.trim) {
            return state.options.trim;
        }
        return [];
    },
    manufacturers(state) {
        if (state.options.manufacturer) {
            return state.options.manufacturer;
        }
        return [];
    },
    userOptions(state) {
        if (state.userOptions) {
            return state.userOptions;
        }
        return [];
    },
    minPrice(state) {
        if (state.options.minPrice) {
            return parseInt(state.options.minPrice);
        }
        return null;
    },
    maxPrice(state) {
        if (state.options.maxPrice) {
            return parseInt(state.options.maxPrice);
        }
        return null;
    },
    minPayment(state) {
        if (typeof state.options.monthlyPayments.min != 'undefined') {
            return parseInt(state.options.monthlyPayments.min);
        }
        return 0;
    },
    maxPayment(state) {
        if (typeof state.options.monthlyPayments.max != 'undefined') {
            return parseInt(state.options.monthlyPayments.max);
        }
        return 2000;
    },
    ages(state) {
        if (state.options.age) {
            // Re-key the array so each key is the age
            let unaggregatdAges = [];
            state.options.age.forEach(age => {
                unaggregatdAges[age.age] = age;
            });

            let count = (typeof unaggregatdAges[0] !== 'undefined') ? unaggregatdAges[0].count : 0;
            let ages = [];
            for (let index = 1; index <= 10; index++) {
                if (typeof unaggregatdAges[index] == 'undefined') {
                    ages.push({
                        age: index,
                        count: count,
                    });
                    continue;
                }
                count += unaggregatdAges[index].count;
                ages.push({
                    age: index,
                    count: count,
                });
            }

            return ages;
        }
        return [];
    },
    fuelTypes(state) {
        if (state.options.fuelType) {
            return state.options.fuelType;
        }
        return [];
    },
    numDoors(state) {
        if (state.options.numDoors) {
            return state.options.numDoors;
        }
        return [];
    },
    bodies(state) {
        if (state.options.body) {
            return state.options.body;
        }
        return [];
    },
    mileages(state) {
        if (state.options.mileage) {
            return state.options.mileage;
        }
        return [];
    },
    transmissions(state) {
        if (state.options.transmission) {
            return state.options.transmission;
        }
        return [];
    },
    colours(state) {
        if (state.options.colour) {
            return state.options.colour;
        }
        return [];
    },
    engineSizes(state) {
        if (state.options.engineSize) {
            return state.options.engineSize.map((size) => {
                return {
                    value: size,
                    label: size + 'L to ' + ((parseFloat(size) * 10 + 2) / 10) + 'L',
                };
            });
        }
        return [];
    },
    sortBys() {
        let sortBys = [];

        if (window.location.search.includes('s_id') || window.location.search.includes('price_alert_id')) {
            sortBys.push({
                value: 'related',
                label: 'Related'
            });
        }

        return [
            ...sortBys,
            {
                value: 'dist',
                label: 'Distance'
            },
            {
                value: 'recommended',
                label: 'Recommended'
            },
            {
                value: 'recent',
                label: 'Recently Added'
            },
            {
                value: 'priceLowest',
                label: 'Price (lowest first)'
            },
            {
                value: 'priceHighest',
                label: 'Price (highest first)'
            },
            {
                value: 'ageYoungest',
                label: 'Age (youngest first)'
            },
            {
                value: 'ageOldest',
                label: 'Age (oldest first)'
            },
            {
                value: 'save',
                label: 'Savings (highest first)'
            },
            {
                value: 'saleEvent',
                label: 'Sale Event'
            }
        ];
    },
    stockSearchSortBys(state) {
        if (state.userOptions.stockSearchPaymentSearch) {
            return [
                {
                    value: 'dist',
                    label: 'Distance'
                },
                {
                    value: 'recent',
                    label: 'Recently Added'
                },
                {
                    value: 'monthlyLowest',
                    label: 'Monthly Payment (lowest first)'
                },
                {
                    value: 'monthlyHighest',
                    label: 'Monthly Payment (highest first)'
                },
                {
                    value: 'priceLowest',
                    label: 'Price (lowest first)'
                },
                {
                    value: 'priceHighest',
                    label: 'Price (highest first)'
                },
                {
                    value: 'save',
                    label: 'Savings (highest first)'
                },
                {
                    value: 'saleEvent',
                    label: 'Sale Event'
                }
            ];
        }

        return [
            {
                value: 'dist',
                label: 'Distance'
            },
            {
                value: 'recent',
                label: 'Recently Added'
            },
            {
                value: 'priceLowest',
                label: 'Price (lowest first)'
            },
            {
                value: 'priceHighest',
                label: 'Price (highest first)'
            },
            {
                value: 'save',
                label: 'Savings (highest first)'
            },
            {
                value: 'saleEvent',
                label: 'Sale Event'
            }
        ];
    },
    loading(state) {
        return state.loading;
    },
    refineSearchVisible(state) {
        return state.refineSearchVisible;
    },
    refineSortVisible(state) {
        return state.refineSortVisible;
    },
    seoTitle(state) {
        return state.seoTitle;
    },
    seoDescription(state) {
        return state.seoDescription;
    },
    dealerLandingPage(state) {
        return state.dealerLandingPage;
    },
};

// actions
const actions = {
    getOptions:
        debounce(({ commit, dispatch, state}) => {
            dispatch('setLoading', true);
            return new Promise((resolve, reject) => {
                axios.post('/ajax/used-vehicles/options', state.userOptions)
                    .then((response) => {
                        dispatch('setLoading', false);
                        dispatch('animateCount', response.data);
                        commit('setOptions', response.data);
                        resolve(response);
                    })
                    .catch((error) => {
                        dispatch('setLoading', false);
                        commit('setOptions', []);
                        reject(error);
                    });
            });
        },
        350,
        {
            leading: true,
            trailing: true
        }
    ),
    getResults:
        debounce(({ commit, dispatch, state}) => {
            let alreadyHaveResults = false;

            // If the options haven't changed, then we already have the results
            if (state.resultsCollected) {
                try {
                    let currentResultsRequest = JSON.parse(state.resultsCollected);
                    alreadyHaveResults = !haveOptionsChanged(currentResultsRequest, state.userOptions);
                } catch (error) {
                    console.error(error);
                }
            }

            if (alreadyHaveResults) {
                return new Promise((resolve, reject) => {
                    commit('setResultsLoading', false);
                    resolve();
                });
            }

            commit('setResultsLoading', true);

            return new Promise((resolve, reject) => {
                UsedVehiclesAPI.getResults(state.userOptions)
                    .then((results) => {
                        commit('setResults', results.data);
                        commit('setPages', results.data);
                        commit('setCount', results.data.total);
                        dispatch('animateCount', {count: results.data.total});
                        // We don't need to collect finance if we're doing payment search. The quotes are embedded in the response already
                        if (results.data.payment_search) {
                            commit('setResultsLoading', false);
                            resolve(results.data);
                            return;
                        }

                        dispatch('getFinance', results.data.current_page);
                        commit('setResultsLoading', false);
                        resolve(results.data);
                    })
                    .catch((error) => {
                        console.error('getResults error', error);
                        commit('setResultsLoading', false);
                        reject();
                    });
            });
        },
        350,
        {
            leading: false,
            trailing: true
        }
    ),
    loadQueryParams({ commit, dispatch, state }, route) {
        if (route.query.model) {
            route.query.preReg = route.meta.preReg;
        }
        const defaults = getDefaultUserOptions();
        let options = {};
        // Step through each item in the query for the route, and determine if it's a valid key or not
        // If it is, then we'll add it to the options we're passing on, otherwise we disregard it
        Object.keys(route.query).forEach((key) => {
            if (route.query[key] == '') {
                if (key != 'emissions' && key != 'annual_tax') {
                    return;
                }
                if (route.query[key] === '') {
                    return;
                }
            }
            const keyType = typeof defaults[key];
            if (keyType == 'object' && defaults[key] !== null) {
                if (typeof route.query[key] == 'object') {
                    options[key] = route.query[key];
                } else {
                    options[key] = [route.query[key]];
                }
            } else if (keyType != 'undefined') {
                options[key] = route.query[key];
            }
        });

        const metaData = GetMetaDataFromRoute(route);

        if (metaData.manufacturer) {
            options.manufacturer = [metaData.manufacturer];
        }
        if (metaData.nearlyNew) {
            options.nearlyNew = metaData.nearlyNew;
            route.query.nearlyNew = metaData.nearlyNew;
        }
        if (metaData.specialOffer) {
            options.specialOffer = metaData.specialOffer;
            route.query.specialOffer = metaData.specialOffer;
        }
        if (metaData.preReg) {
            options.preReg = 1;
            route.query.preReg = metaData.preReg;
        }
        if (metaData.newStockSearch) {
            options.new = (metaData.newStockSearch === true) ? 1 : 0;
            route.query.new = (metaData.newStockSearch === true) ? 1 : 0;
        }

        if (route.name == 'dealer-landing-page' && window.usedDealer && typeof window.usedDealer.slug != 'undefined' && window.usedDealer.slug == route.params.slug) {
            options.d = window.usedDealer.id;
            commit('setSeoTitle', window.usedDealer.used_seo_title);
            commit('setSeoDescription', window.usedDealer.used_seo_description);
        } else if ((route.name == 'branded-cms-landing-page' || route.name == 'cms-landing-page') && state.usedVehicleModelLandingPageData != null) {
            if (route.params.slug == state.usedVehicleModelLandingPageData.slug && ((route.name == 'branded-cms-landing-page' && route.params.brand == state.usedVehicleModelLandingPageData.manufacturer_slug) || route.name == 'cms-landing-page')) {
                commit('setSeoTitle',state.usedVehicleModelLandingPageData.seo_title);
                commit('setSeoDescription',state.usedVehicleModelLandingPageData.seo_description);
                Object.keys(state.usedVehicleModelLandingPageData.query).forEach(key => {
                    if (typeof state.userOptions[key] == 'undefined') {
                        return;
                    }

                    if (typeof state.userOptions[key] == 'object') {
                        if (typeof state.usedVehicleModelLandingPageData.query[key] == 'object') {
                            if (typeof options[key] == 'undefined' && state.usedVehicleModelLandingPageData.query[key].length > 0) {
                                options[key] = state.usedVehicleModelLandingPageData.query[key];
                                return;
                            }
                            // If the userOptions is an object, merge in any of the used landing page values that aren't already part of the array
                            Object.keys(state.usedVehicleModelLandingPageData.query[key]).forEach(subKey => {
                                if (options[key].indexOf(state.usedVehicleModelLandingPageData.query[key][subKey]) === -1) {
                                    if (typeof options[key] == 'undefined') {
                                        options[key] = [];
                                    }
                                    options[key].push(state.usedVehicleModelLandingPageData.query[key][subKey]);
                                }
                            });
                            return;
                        }

                        if (typeof options[key] == 'undefined' && state.usedVehicleModelLandingPageData.query[key] != defaults[key]) {
                            options[key] = state.usedVehicleModelLandingPageData.query[key];
                            return;
                        }
                        // If the userOptions is an object but the used landing page data isn't, then push the used landing page value onto the array (if it's not already in there)
                        if (options[key].indexOf(state.usedVehicleModelLandingPageData.query[key]) === -1) {
                            if (typeof options[key] == 'undefined') {
                                options[key] = [];
                            }
                            options[key].push(state.usedVehicleModelLandingPageData.query[key]);
                        }
                        return;
                    }

                    options[key] = state.usedVehicleModelLandingPageData.query[key];
                    return;
                });
            } else {
                commit('setSeoTitle', null);
                commit('setSeoDescription', null);
            }
        } else {
            commit('setSeoTitle', null);
            commit('setSeoDescription', null);
        }

        // This flag tells UsedSearchOptions/updateOptions to treat the incoming options object as a complete set of options. When this flag is false, updateOptions will fill state.userOptions with default options and only append the incoming options passed in
        options.fullOptions = true;
        return dispatch('updateOptions', options);
    },

    updateOptions({ commit, dispatch, state }, options) {
        // If the distance changes, we need to rebuild the dealers
        if (typeof options.distance !== 'undefined' && typeof options.fullOptions == 'undefined') {
            // Store the updated distance in the state
            commit('setUserOptions', options);
            // Trigger the location updated method so the nearest dealers recalculates
            dispatch('distanceUpdated', options.distance);
            // distanceUpdated triggers updateOptions itself, so we return here to prevent collecting options from the server twice in quick succession
            return;
        }

        // If the updateOptions call has the fullOptions property, we're updating the full set of options, not just a single key
        // Since the incoming options might not be a full list of every option, we should recreate any missing keys using the default values
        // This is how we handle loading query parameters on the search results page. The route changes, we watch the route in used-results.vue, and we trigger updateOptions based on the query params
        if (typeof options.fullOptions !== 'undefined') {
            options = fillMissingKeysWithDefaultValues(options, state);
        }

        // Check if the options have changed
        let isChangedOptions = haveOptionsChanged(state.userOptions, options);
        // Update the state of the options
        commit('setUserOptions', options);

        // If the search has changed in some way (excluding page/sort/size)
        // Then reset our results set and start again
        // But if it's the first load, don't reset results, as this would prevent from deep linking to a certain page
        if (!state.firstLoad && !isChangedOptions) {
            commit('resetResults');
        }
        commit('setFirstLoad', false);
        return dispatch('getOptions');
    },
    resetUserOptionsAndCollectOptions({commit, dispatch, state}, preserveOptions) {
        let preserve = {};
        if (typeof preserveOptions !== 'undefined') {
            preserveOptions.forEach(option => {
                preserve[option] = state.userOptions[option];
            });
        }
        dispatch('resetUserOptions');
        commit('setUserOptions', preserve);
        dispatch('getOptions');
    },
    setLoading({ commit, state }, loading) {
        if (loadingTimeout !== false) {
            clearTimeout(loadingTimeout);
            loadingTimeout = false;
        }

        if (!loading) {
            commit('setLoading', false);
            return;
        }

        loadingTimeout = setTimeout(() => {
            commit('setLoading', true);
        }, 300);
    },
    getFinance({ commit, state }, page) {
        return new Promise((resolve, reject) => {
            const usedVehicleFinance = new UsedVehicleFinance();
            if (state.userOptions.paymentSearch == 1) {
                usedVehicleFinance.setTerm(state.userOptions.term);
                usedVehicleFinance.setDeposit(state.userOptions.deposit);
                usedVehicleFinance.setMileage(state.userOptions.annualMileage);
            } else {
                usedVehicleFinance.setTerm(state.defaultTerm);
                usedVehicleFinance.setDeposit(state.defaultDeposit);
                usedVehicleFinance.setMileage(state.defaultAnnualMileage);
            }
            usedVehicleFinance.setVehicles(state.results);
            usedVehicleFinance.getFinance()
                .then(vehicles => {
                    commit('updateResultsFinance', vehicles);
                    resolve();
                })
                .catch(vehicles => {
                    commit('updateResultsFinance', vehicles);
                    reject();
                });
        });
    },
    resetUserOptionsByPushingRoute({ commit, state, dispatch }, stockSearch = false) {
        if (JSON.stringify(router.currentRoute.query) == '{}') {
            return;
        }
        // Only update query params that are part of state.userOptions
        dispatch('resetUserOptions');
        commit('resetResults');

        if (stockSearch) {
            router.push({
                name: 'stock-search-results',
                query: {},
            }).catch(error => {
                console.error(error);
            });
            return;
        }

        if (state.dealerLandingPage !== null) {
            router.push({
                name: 'dealer-landing-page',
                params: {
                    slug: state.dealerLandingPage.slug
                }
            }).catch(error => {
                console.error(error);
            });
            return;
        }

        router.push({
            name: 'used-results',
            query: {},
            params: {
                resultsType: 'used-vehicles'
            },
        }).catch(error => {
            console.error(error);
        });
    },
    // setUserOptionsByPushingRoute:
    //     debounce(({ commit, state }, options) => {
    setUserOptionsByPushingRoute({ commit, state }, options) {
        // newQuery will hold the full state of user options, but will only hold values that differ from the getDefaultUserOptions();
        // by only holding non default values, we can avoid putting default values into the query string, preventing a bunch of things like mileage=&colour=&fuel= etc
        let newQuery = {};
        let changed = false;
        let navigateWithoutReset = false;
        const defaults = getDefaultUserOptions();

        // Create a new query that is prefilled with non-blank options from the current state.userOptions
        Object.keys(state.userOptions).forEach((key) => {
            if (typeof defaults[key] != 'undefined' && state.userOptions[key] === defaults[key]) {
                return;
            }

            newQuery[key] = state.userOptions[key];
        });

        // Only update query params that are part of state.userOptions (a valid key) and have a value
        Object.keys(options).forEach((key) => {
            // Don't add the value if it's not an available option in state.userOptions
            if (typeof state.userOptions[key] == 'undefined' || typeof defaults[key] == 'undefined') {
                return;
            }

            // Don't add the value if it's the same as default
            // Unless we're changing from a non blank to a blank, because we should then blank whatever is in the state
            if (options[key] === defaults[key]) {
                // Remove the key as we're changing it to blank
                if (typeof newQuery[key] != 'undefined') {
                    delete newQuery[key];
                }
                // If the default value isn't in the current user options, we need to log that there is a change, because the current options might be something like model=>'108', and is now model => '', but because '' is the default value it won't be added to the newQuery and therefore wouldn't normally be logged as a change
                if (state.userOptions[key] !== options[key]) {
                    changed = true;
                }
                return;
            }

            // if (key == 'hideReserved' && options[key] === false) {
            //     if (state.userOptions[key] != options[key]) {
            //         changed = true;
            //     }

            if (key == 'minPrice' && (options[key] == 0 || options[key] == state.options.minPrice)) {
                if (state.userOptions[key] != options[key]) {
                    changed = true;
                }
                delete newQuery[key];
                return;
            }
            if (key == 'maxPrice' && (options[key] == 0 || options[key] == state.options.maxPrice)) {
                if (state.userOptions[key] != options[key]) {
                    changed = true;
                }
                delete newQuery[key];
                return;
            }

            if (typeof defaults[key] == 'object' && defaults[key] !== null) {
                if (typeof options[key] != 'object') {
                    newQuery[key] = [options[key]];
                } else {
                    newQuery[key] = options[key];
                }
            } else {
                newQuery[key] = options[key];
            }

            if (key == 'page') {
                navigateWithoutReset = true;
            } else {
                changed = true;
            }
        });

        // Preserve any utm campaigns in the URL
        if (typeof router.currentRoute.query.utm_source != 'undefined') {
            newQuery.utm_source = router.currentRoute.query.utm_source;
        }
        if (typeof router.currentRoute.query.utm_medium != 'undefined') {
            newQuery.utm_medium = router.currentRoute.query.utm_medium;
        }
        if (typeof router.currentRoute.query.utm_campaign != 'undefined') {
            newQuery.utm_campaign = router.currentRoute.query.utm_campaign;
        }

        // If navigateWithoutReset is true, then the page has changed and we should navigate without resetting the page
        // Else if the query has changed in some way (other than the page), then reset paging to page 1
        if (navigateWithoutReset) {
            // If the page is changed, and the rest of the query is the same, then don't reset before updating options
            const newRoute = getOptimisedRouteFromRoute(
                {
                    name: router.currentRoute.name,
                    query: newQuery,
                    params: router.currentRoute.params
                },
                state.usedVehicleModelLandingPageData
            );
            router.push({
                name: newRoute.name,
                params: newRoute.params,
                query: newRoute.query,
            });
            return true;
        }

        if (changed) {
            delete newQuery.page;
            commit('resetResults');
            commit('setResultsLoading', true);
            const newRoute = getOptimisedRouteFromRoute(
                {
                    name: router.currentRoute.name,
                    query: newQuery,
                    params: router.currentRoute.params
                },
                state.usedVehicleModelLandingPageData
            );

            router.push(newRoute);
            return true;
        }

        return false;
    },
    //     300,
    //     {
    //         leading: true,
    //         trailing: true
    //     }
    // ),
    animateCount({ commit, state }, options) {
        if (typeof options.count == 'undefined') {
            return;
        }
        // If the value of the count has changed, we need to initialize the animation of the number
        if (options.count !== animatedCountDestination) {
            // If we don't have a TWEEN object setup already then set it up
            if (!animatedCountTween) {
                let countObject = {count:0};
                animatedCountTween = new TWEEN.Tween(countObject);
                animatedCountTween.to({count: options.count}, 500)
                .onUpdate(function(countObject) {
                    commit('setAnimatedCount', countObject.count);
                })
                .onComplete(function() {
                    cancelAnimationFrame(animationFrame);
                })
                .onStop(function() {
                    cancelAnimationFrame(animationFrame);
                });
                animatedCountTween.start();
                animatedCountDestination = options.count;

                animate();
                return;
            }
            // Otherwise use the existing TWEEN object
            animatedCountTween.stop();
            animatedCountTween.to({count: options.count}, 500);
            animatedCountTween.start();
            animatedCountDestination = options.count;

            animate();
        }
    },
    distanceUpdated({rootState, state, dispatch}, newDistance) {
        let dealers = state.usedDealers;

        // If the distance isn't "Any", remove the dealers that aren't within the distance
        if (newDistance > 0 && rootState.Dealers.haveLocation) {
            dealers = dealers.filter((dealer) => {
                if(dealer['is_national_centre'] == 1) {
                    return true;
                }
                const distance = calculateDistance(rootState.Dealers.lat, rootState.Dealers.lng, dealer.lat, dealer.lng);
                return distance <= newDistance;
            });
            if (dealers.length == 0) {
                dealers = 'none';
            } else {
                dealers = dealers.sort((a, b) => {
                    if (a.distance < b.distance) {
                        return -1;
                    }
                    return (a.distance == b.distance) ? 0 : 1;
                })
            }
        }

        // Reduce the dealer list to just the id
        if (typeof dealers !== 'string') {
            dealers = dealers.map((dealer) => {
                return dealer.id;
            });
        }

        dispatch('updateOptions', {
            d: dealers,
        });
    },
    locationUpdated({rootState, state, dispatch, commit}, options) {
        const triggerOptionsUpdate = (typeof options != 'undefined' && options !== null && typeof options.triggerOptionsUpdate != 'undefined' && options.triggerOptionsUpdate === true) ? true : false;
        let dealers = state.usedDealers;
        // If the distance isn't "Any", remove the dealers that aren't within the distance
        if (rootState.Dealers.haveLocation) {
            if (state.userOptions.distance > 0) {
                try {
                    dealers = dealers.filter((dealer, index) => {
                        if(dealer['is_national_centre'] == 1) {
                            return true;
                        }
                        const distance = calculateDistance(rootState.Dealers.lat, rootState.Dealers.lng, dealer.lat, dealer.lng);
                        return distance <= state.userOptions.distance;
                    });
                } catch (error) {
                    console.error(error);
                };
            }
            if (dealers.length == 0) {
                dealers = 'none';
            } else {
                // To prevent modifying the rootState.Dealers.dealers property, we have to deep copy it now
                dealers = JSON.parse(JSON.stringify(dealers));
                dealers = dealers.sort((a, b) => {
                    if (a.distance < b.distance) {
                        return -1;
                    }
                    return (a.distance == b.distance) ? 0 : 1;
                })
            }
            if (!state.sortByModified) {
                commit('setSortBy', 'dist');
            }
        }

        // Reduce the dealer list to just the id
        if (typeof dealers !== 'string') {
            dealers = dealers.map((dealer) => {
                return dealer.id;
            });
        }

        // If we have all dealers in the filtered dealer list, then set the dealers array to [].
        // We don't need to pass a list of filtered dealers if every single dealer is in the list
        // If there are actually no dealers, then the dealers string will be "none" instead of []
        // if (dealers.length == rootState.Dealers.dealers.length) {
        //     if (triggerOptionsUpdate) {
        //         return dispatch('updateOptions', {
        //             d: [],
        //         });
        //     }
        //     commit('setUserOptions', {
        //         d: [],
        //     });
        //     return;
        // }

        if (triggerOptionsUpdate) {
            const updateViaRoute = (typeof options.updateViaRoute != 'undefined' && options.updateViaRoute === true) ? 'setUserOptionsByPushingRoute' : 'updateOptions';
            return dispatch(updateViaRoute, {
                d: dealers,
            });
        }
        commit('setUserOptions', {
            d: dealers,
        });
        return;
    },
    resetUserOptions({rootState, state, commit}) {
        let options = getDefaultUserOptions();
        if (rootState.Dealers.haveLocation && !state.sortByModified) {
            options.sortBy = 'dist';
            options.stockSearchSortBy = 'dist';
        }
        options.minPayment = state.options.monthlyPayments.min;
        options.maxPayment = state.options.monthlyPayments.max;

        delete options.d;
        commit('setUserOptions', options);
        commit('setSortBy', options.sortBy);
    },
};

const mutations = {
    setLandingPageData(state) {
        if (typeof window.usedLandingPage == 'undefined') {
            return;
        }

        let query = {};
        try {
            query = JSON.parse(window.usedLandingPage.query);
        } catch (error) {
            console.error('Error decoding window.usedLandingPage query JSON', window.usedLandingPage.query);
            query = {};
        }

        state.usedVehicleModelLandingPageData = {
            ...window.usedLandingPage,
            query
        };

        state.seoTitle = (state.usedVehicleModelLandingPageData.seo_title) ? state.usedVehicleModelLandingPageData.seo_title : 'Stellantis &You UK | Used Vehicles';
        state.seoDescription = (state.usedVehicleModelLandingPageData.seo_description) ? state.usedVehicleModelLandingPageData.seo_description : 'Stellantis &You UK | Used Vehicles'

        document.querySelector('title').innerHTML = (state.usedVehicleModelLandingPageData.seo_title) ? state.usedVehicleModelLandingPageData.seo_title : 'Stellantis &You UK | Used Vehicles';
        document.querySelector('meta[name="description"]').setAttribute('content', (state.usedVehicleModelLandingPageData.seo_description) ? state.usedVehicleModelLandingPageData.seo_description : 'Stellantis &You UK | Used Vehicles');
    },

    clearDealerLandingPageData(state) {
        state.dealerLandingPage = null;
        state.seoTitle = null;
        state.seoDescription = null;
    },

    setDealerLandingPageData(state) {
        if (typeof window.usedDealer == 'undefined') {
            return;
        }

        state.dealerLandingPage = window.usedDealer;
        state.seoTitle = (window.usedDealer.used_seo_title) ? window.usedDealer.used_seo_title : 'Stellantis &You UK | Used Vehicles';
        state.seoDescription = (window.usedDealer.used_seo_description) ? window.usedDealer.used_seo_description : 'Stellantis &You UK | Used Vehicles'

        document.querySelector('title').innerHTML = state.seoTitle;
        document.querySelector('meta[name="description"]').setAttribute('content', state.seoDescription);
    },

    setUserOptions(state, options) {
        if (typeof options !== 'object') {
            return false;
        }
        let changed = false;
        Object.keys(options).forEach((key) => {
            if (typeof state.userOptions[key] == 'undefined') {
                return;
            }
            if (key == 'sortBy' && state.userOptions[key] != options[key]) {
                state.sortByModified = true;
            }
            state.userOptions[key] = options[key];
            changed = true;
        });
        // if (typeof state.userOptions['manufacturer'] !== 'undefined' && state.userOptions['manufacturer'] == '') {
        //     state.userOptions['model'] = '';
        // }
        return changed;
    },
    setSortBy(state, option) {
        state.userOptions.sortBy = option;
    },
    setResults(state, results) {
        if (typeof results.data == 'undefined') {
            state.results = [];
            state.haveResults = false;
            state.resultsCollected = '{}';
            return;
        }
        if (typeof results.payment_search == 'undefined' || !results.payment_search) {
            state.results = results.data;
            // Sets the "hash" of the results, used later to determine if we've already collected results
            state.resultsCollected = JSON.stringify(state.userOptions);
            state.haveResults = (state.results.length > 0) ? true : false;
            return;
        }

        // If it's a payment search, we need to combine the used vehicle results and the payment search results together
        let resultsKeyedById = {};
        results.v.forEach(vehicle => {
            resultsKeyedById[vehicle.id] = vehicle;
        });
        let combinedResults = [];
        results.data.forEach(vehicle => {
            const id = vehicle.Vehicle.Physical.ExternalVehicleId || 0;
            if (typeof resultsKeyedById[id] !== 'undefined') {
                combinedResults.push({
                    ...resultsKeyedById[id],
                    payment_search: 1,
                    finance: {
                        FinanceProductResults: vehicle.Finance || []
                    },
                });
            }
        });
        state.results = combinedResults;
        // Sets the "hash" of the results, used later to determine if we've already collected results
        state.resultsCollected = JSON.stringify(state.userOptions);
        state.haveResults = (state.results.length > 0) ? true : false;
    },
    updateResultsFinance(state, updatedVehicles) {
        // Step through each vehicle in the results we currently have
        for (let i = 0, len = state.results.length; i < len; i += 1) {
            if (typeof state.results[i] == 'undefined' || !state.results[i]) {
                continue;
            }
            // Go through each finance result from CW
            for (let j = 0, len = updatedVehicles.length; j < len; j += 1) {
                // If the CW result ID matches the vehicle id, then update the finance key of that vehicle using Vue.set so that the reactivity works
                if (state.results[i].id == updatedVehicles[j].id) {
                    Vue.set(state.results[i], 'finance', ((updatedVehicles[j].finance) ? updatedVehicles[j].finance : false));
                }
            }
        }
    },
    resetResults(state) {
        state.results = [];
        state.resultsCollected = '';
        state.haveResults = false;
        state.pages = [];
    },
    setPages(state, data) {
        delete data.data;
        Vue.set(state, 'pages', data);
    },
    setOptions(state, options) {
        Object.keys(options).forEach(key => {
            if (typeof options[key] !== 'object') {
                return;
            }

            if (!state.userOptions[key] || key == 'carOrVan' || key == 'mileage' || key == 'engineSize') {
                return;
            }

            // Check to see if the currently selected user option is in the list of available options. If it's not then add it to the end of the list of available options, and give it a count of 0
            // By doing this, we can avoid user selection being forgotten when an unavailable combination is selected
            let found = false;
            // const optionsLength = options[key].length;
            // const isObject = typeof state.userOptions[key] == 'object';
            // for (let index = 0; index < optionsLength; index++) {
            //     const el = options[key][index];
            //     if (isObject) {
            //         if (el[key].indexOf == state.userOptions[key]) {
            //             found = true;
            //             break;
            //         }
            //         continue;
            //     }
            //     if (el[key] == state.userOptions[key]) {
            //         found = true;
            //         break;
            //     }
            // }
            if (typeof state.userOptions[key] == 'object') {
                state.userOptions[key].forEach(userOption => {
                    // The option will either be an object with the count attached to it, or just a string
                    if (options[key].find(option => {
                        if (typeof option == 'object') {
                            return option[key] == userOption;
                        } else {
                            return option == userOption;
                        }
                    })) {
                        return;
                    }
                    options[key].push({
                        [key]: userOption,
                        count: 0,
                    });
                });
            } else {
                let hasCount = false;
                options[key].forEach(el => {
                    if (typeof el == 'object') {
                        hasCount = true;
                        if (el[key] == state.userOptions[key]) {
                            found = true;
                        }
                        return;
                    }
                    if (el == state.userOptions[key]) {
                        found = true;
                    }
                });
                if (!found) {
                    if (hasCount) {
                        let o = {};
                        o[key] = state.userOptions[key];
                        o.count = 0;
                        options[key].push(o);
                    } else {
                        options[key].push(state.userOptions[key]);
                    }
                }
            }
        });
        state.options = options;
    },
    setCount(state, count) {
        state.options.count = parseInt(count);
    },
    setAnimatedCount(state, count) {
        state.animatedCount = count;
    },
    setLoading(state, loading) {
        state.loading = loading;
    },
    setResultsLoading(state, loading) {
        state.resultsLoading = loading;
    },
    toggleRefineSearchVisible(state) {
        state.refineSearchVisible = !state.refineSearchVisible;
    },
    toggleRefineSortVisible(state) {
        state.refineSortVisible = !state.refineSortVisible;
    },
    closeRefineSearchVisible(state) {
        state.refineSearchVisible = false;
    },
    closeRefineSortVisible(state) {
        state.refineSortVisible = false;
    },
    setFirstLoad(state, v) {
        state.firstLoad = v;
    },
    setLastResultsUrl(state, url) {
        state.lastResultsUrl = url;
        let storedUrl = {};
        if (typeof url.name !== 'undefined') {
            storedUrl.name = url.name;
        }
        if (typeof url.query !== 'undefined') {
            storedUrl.query = url.query;
        }
        if (typeof url.params !== 'undefined') {
            storedUrl.params = url.params;
        }
        if (Object.keys(storedUrl).length > 0 && storedUrl.constructor === Object) {
            saveToStorage('lastResultsUrl', JSON.stringify(storedUrl));
        }
    },

    clearNotification(state, notification) {
        let newNotifications = [];
        state.notifications.forEach(originalNotification => {
            if (originalNotification != notification) {
                newNotifications.push(originalNotification);
            }
        });
        state.notifications = newNotifications;
    },

    clearAllNotifications(state) {
        state.notifications = [];
    },

    addNotification(state, notification) {
        let newNotifications = [];
        state.notifications.forEach(originalNotification => {
            if (originalNotification != notification) {
                newNotifications.push(originalNotification);
            }
        });
        newNotifications.push(notification);
        state.notifications = newNotifications;
    },

    setUsedDealers(state, usedDealers) {
        state.usedDealers = usedDealers;
    },

    setSeoTitle(state, value) {
        state.seoTitle = value;
        if (value != null) {
            document.querySelector('title').innerHTML = value;
        }
    },

    setSeoDescription(state, value) {
        state.seoDescription = value;
        if (value != null) {
            document.querySelector('meta[name="description"]').setAttribute('content', value);
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};